import React, { useState, useEffect, useCallback } from 'react';

interface BetaSignup {
  id: string;
  name: string;
  email: string;
  platform?: 'ios' | 'android';
  experienceYears: '0-1' | '2-5' | '6-10' | '10+';
  timestamp: string;
  status: string;
}

interface BetaDashboardProps {
  isVisible: boolean;
  onClose: () => void;
}

export default function BetaDashboard({ isVisible, onClose }: BetaDashboardProps) {
  const [signups, setSignups] = useState<BetaSignup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);



  const [adminToken, setAdminToken] = useState<string>('');

  const fetchSignups = useCallback(async () => {
    if (!adminToken) {
      setError('Please enter admin token first');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('https://app.controller-app.com/api/v1/beta-signups', {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSignups(data.signups || []);
      } else if (response.status === 401) {
        setError('Invalid admin token');
      } else {
        setError('Failed to fetch signups');
      }
    } catch (err) {
      setError('Error fetching signups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (isVisible) {
      fetchSignups();
    }
  }, [isVisible, fetchSignups]);

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Platform', 'Experience Years', 'Signup Date', 'Status'];
    const csvContent = [
      headers.join(','),
      ...signups.map(signup => [
        `"${signup.name}"`,
        `"${signup.email}"`,
        signup.platform || 'N/A',
        signup.experienceYears,
        new Date(signup.timestamp).toLocaleDateString(),
        signup.status
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `beta-signups-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const generateAppleConnectCSV = async () => {
    if (!adminToken) {
      setError('Please enter admin token first');
      return;
    }

    try {
      const response = await fetch('https://app.controller-app.com/api/v1/testflight/apple-connect-csv', {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const csvContent = await response.text();
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `apple-connect-beta-testers-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        setError('Failed to generate Apple Connect CSV');
      }
    } catch (err) {
      setError('Error generating Apple Connect CSV');
      console.error(err);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="beta-dashboard-overlay">
      <div className="beta-dashboard">
        <div className="dashboard-header">
          <h2>Beta Signups Dashboard</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        
        <div className="dashboard-stats">
          <div className="stat-card">
            <h3>Total Signups</h3>
            <p className="stat-number">{signups.length}</p>
          </div>
          <div className="stat-card">
            <h3>iOS Signups</h3>
            <p className="stat-number">{signups.filter(s => s.platform === 'ios').length}</p>
          </div>
          <div className="stat-card">
            <h3>Android Signups</h3>
            <p className="stat-number">{signups.filter(s => s.platform === 'android').length}</p>
          </div>
          <div className="stat-card">
            <h3>Beginners (0-1y)</h3>
            <p className="stat-number">{signups.filter(s => s.experienceYears === '0-1').length}</p>
          </div>
          <div className="stat-card">
            <h3>Intermediate (2-5y)</h3>
            <p className="stat-number">{signups.filter(s => s.experienceYears === '2-5').length}</p>
          </div>
          <div className="stat-card">
            <h3>Advanced (6-10y)</h3>
            <p className="stat-number">{signups.filter(s => s.experienceYears === '6-10').length}</p>
          </div>
          <div className="stat-card">
            <h3>Expert (10+y)</h3>
            <p className="stat-number">{signups.filter(s => s.experienceYears === '10+').length}</p>
          </div>
        </div>

        <div className="dashboard-actions">
          <div className="admin-token-input">
            <input
              type="password"
              placeholder="Enter Admin Token"
              value={adminToken}
              onChange={(e) => setAdminToken(e.target.value)}
              className="form-input"
            />
          </div>
          <button onClick={fetchSignups} className="refresh-button">
            Refresh
          </button>
          <button onClick={exportToCSV} className="export-button">
            Export to CSV
          </button>
          <button onClick={generateAppleConnectCSV} className="apple-connect-button">
            Apple Connect CSV
          </button>
        </div>

        {loading && <div className="loading">Loading signups...</div>}
        {error && <div className="error">{error}</div>}

        <div className="signups-table">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Platform</th>
                <th>Experience</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {signups.map(signup => (
                <tr key={signup.id}>
                  <td>{signup.name}</td>
                  <td>{signup.email}</td>
                  <td>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '0.25rem',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      background: signup.platform === 'ios' ? 'rgba(0, 122, 255, 0.1)' : 'rgba(52, 211, 153, 0.1)',
                      color: signup.platform === 'ios' ? '#007AFF' : '#10B981'
                    }}>
                      {signup.platform || 'N/A'}
                    </span>
                  </td>
                  <td>{signup.experienceYears}</td>
                  <td>{new Date(signup.timestamp).toLocaleDateString()}</td>
                  <td>
                    <span className={`status-badge status-${signup.status}`}>
                      {signup.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
